import { useEffect, useRef, useState } from 'react';
import { Alert, V2Button, V2Card, V2CardHeader } from '@sakhtar/design-system/react';
import { ApiError, apiGet, apiPost } from '../../lib/apiClient.js';
import {
  buildDefaultDatasetMapping,
  datasetFieldLabel,
  validateDatasetMapping,
} from '../../lib/dataImport/datasetMapping.js';
import {
  readDatasetWorkbookPreview,
  type DatasetSheetPreview,
} from '../../lib/dataImport/datasetWorkbook.js';
import type {
  DatasetCommitResult,
  DatasetDryRun,
  DatasetEntitiesResponse,
  DatasetRowsPage,
  DatasetRow,
} from '../../lib/types/dataImportExport.js';

interface Selection {
  readonly sheetIndex: number;
  readonly sheetName: string;
  readonly entityClass: string;
  readonly mapping: Record<string, string>;
}
interface Batch {
  readonly workbookBatchId: string;
  readonly children: readonly (Selection & { readonly importJobId: string })[];
  readonly skippedSheets: readonly { readonly sheetName: string; readonly sheetIndex: number }[];
}
interface Preview {
  readonly previewToken: string;
  readonly errorCount: number;
  readonly warningCount: number;
  readonly children: readonly { readonly importJobId: string; readonly dryRun: DatasetDryRun }[];
}
interface Result {
  readonly workbookBatchId: string;
  readonly replayed: boolean;
  readonly children: readonly DatasetCommitResult[];
}

function message(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) return 'برای این عملیات دسترسی ندارید.';
  if (error instanceof Error) return error.message;
  return 'ارتباط با سرور برقرار نشد؛ دوباره تلاش کنید.';
}
async function base64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary);
}

/** Explicit workbook orchestration; the server owns atomicity, permissions and dependencies. */
export function DatasetWorkbookFlow({
  base,
  data,
  onBack,
}: {
  readonly base: string;
  readonly data: DatasetEntitiesResponse;
  readonly onBack: () => void;
}) {
  const allowed = data.items.filter(
    (entity) =>
      ['party', 'product', 'uom'].includes(entity.entityClass) &&
      entity.availability === 'ready' &&
      entity.permitted,
  );
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<readonly DatasetSheetPreview[]>([]);
  const [selections, setSelections] = useState<Record<number, Selection>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [rows, setRows] = useState<Record<string, readonly DatasetRow[]>>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const generation = useRef(0);
  const locked = useRef(false);
  const key = useRef<string | null>(null);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const path = `${base}/api/v1/import/workbooks/jobs`;

  function invalidate() {
    generation.current += 1;
    key.current = null;
    setBatch(null);
    setPreview(null);
    setRows({});
    setResult(null);
    setAcknowledged(false);
    setError(null);
  }
  async function chooseFile(chosen: File) {
    if (locked.current) return;
    invalidate();
    setFile(null);
    setSheets([]);
    setSelections({});
    const version = generation.current;
    locked.current = true;
    setBusy(true);
    try {
      if (!chosen.name.toLowerCase().endsWith('.xlsx'))
        throw new Error('برای ورود چندشیتی فایل XLSX انتخاب کنید.');
      if (chosen.size > data.maxSourceFileBytes) throw new Error('حجم فایل بیش از حد مجاز است.');
      const workbook = await readDatasetWorkbookPreview(chosen);
      if (version !== generation.current) return;
      setFile(chosen);
      setSheets(workbook.sheets);
    } catch (cause) {
      if (version === generation.current) setError(message(cause));
    } finally {
      locked.current = false;
      if (version === generation.current) setBusy(false);
    }
  }
  function selectSheet(sheet: DatasetSheetPreview, entityClass: string) {
    if (locked.current) return;
    invalidate();
    setSelections((before) => {
      const next = { ...before };
      delete next[sheet.index];
      const entity = allowed.find((item) => item.entityClass === entityClass);
      if (entity && !sheet.error)
        next[sheet.index] = {
          sheetIndex: sheet.index,
          sheetName: sheet.name,
          entityClass,
          mapping: buildDefaultDatasetMapping(sheet.headers, entity.writableFields),
        };
      return next;
    });
  }
  function mapField(index: number, field: string, source: string) {
    if (locked.current) return;
    invalidate();
    setSelections((before) => {
      const selection = before[index];
      if (!selection) return before;
      const mapping = { ...selection.mapping };
      delete mapping[field];
      if (source) mapping[field] = source;
      return { ...before, [index]: { ...selection, mapping } };
    });
  }
  const selected = Object.values(selections).sort((a, b) => a.sheetIndex - b.sheetIndex);
  const validation = selected.flatMap((selection) => {
    const sheet = sheets.find((item) => item.index === selection.sheetIndex);
    const entity = allowed.find((item) => item.entityClass === selection.entityClass);
    if (!sheet || !entity || sheet.error) return ['انتخاب شیت نامعتبر است.'];
    return validateDatasetMapping(
      selection.mapping,
      sheet.headers,
      entity.writableFields,
      entity.requiredFields,
    ).map((text) => `${sheet.name}: ${text}`);
  });
  async function dryRun() {
    if (locked.current || !file || !selected.length || validation.length) return;
    locked.current = true;
    setBusy(true);
    setError(null);
    const version = generation.current;
    try {
      key.current ??= crypto.randomUUID();
      const created = await apiPost<Batch>(
        path,
        { fileName: file.name, contentBase64: await base64(file), selections: selected },
        { headers: { 'idempotency-key': key.current } },
      );
      if (version !== generation.current) return;
      setBatch(created);
      const checked = await apiPost<Preview>(`${path}/${created.workbookBatchId}/dry-run`);
      if (version !== generation.current) return;
      setPreview(checked);
      setAcknowledged(false);
      const diagnostics = await Promise.all(
        checked.children.map(async (child) => {
          const [errors, warnings] = await Promise.all([
            apiGet<DatasetRowsPage>(
              `${base}/api/v1/import/jobs/${child.importJobId}/rows?status=error&limit=100`,
            ),
            apiGet<DatasetRowsPage>(
              `${base}/api/v1/import/jobs/${child.importJobId}/rows?status=warning&limit=100`,
            ),
          ]);
          return [child.importJobId, [...errors.items, ...warnings.items]] as const;
        }),
      );
      if (version === generation.current) setRows(Object.fromEntries(diagnostics));
    } catch (cause) {
      if (version === generation.current) {
        setPreview(null);
        setError(message(cause));
      }
    } finally {
      locked.current = false;
      if (version === generation.current) setBusy(false);
    }
  }
  async function commit() {
    if (
      locked.current ||
      !batch ||
      !preview ||
      preview.errorCount ||
      (preview.warningCount && !acknowledged)
    )
      return;
    locked.current = true;
    setBusy(true);
    setError(null);
    const version = generation.current;
    try {
      await apiPost(`${path}/${batch.workbookBatchId}/confirm`, {
        previewToken: preview.previewToken,
        acknowledgedErrorCount: preview.errorCount,
        acknowledgedWarningCount: preview.warningCount,
      });
      const committed = await apiPost<Result>(`${path}/${batch.workbookBatchId}/commit`);
      if (version === generation.current) setResult(committed);
    } catch (cause) {
      if (version === generation.current) setError(message(cause));
    } finally {
      locked.current = false;
      if (version === generation.current) setBusy(false);
    }
  }
  const ignored = sheets.filter((sheet) => !selections[sheet.index]);
  return (
    <V2Card>
      <V2CardHeader title="ورود فایل چندشیتی" />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Alert tone="info">
          نوع داده و ستون‌های هر شیت را خودتان انتخاب کنید. شیت‌های انتخاب‌نشده نادیده گرفته
          می‌شوند. با وجود خطا در هر شیت، هیچ شیتی ثبت نمی‌شود.
        </Alert>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {!result ? (
          <>
            <label>
              فایل Excel چندشیتی{' '}
              <input
                type="file"
                accept=".xlsx"
                disabled={busy}
                onChange={(event) => {
                  const chosen = event.target.files?.[0];
                  if (chosen) void chooseFile(chosen);
                  event.target.value = '';
                }}
              />
            </label>
            {file ? <p>{file.name}</p> : null}
            {sheets.map((sheet) => {
              const selection = selections[sheet.index];
              const entity = allowed.find((item) => item.entityClass === selection?.entityClass);
              return (
                <fieldset key={sheet.index} disabled={busy} style={{ padding: 16 }}>
                  <legend>
                    {sheet.name} — {sheet.rowCount.toLocaleString('fa-IR')} ردیف
                  </legend>
                  {sheet.error ? (
                    <Alert tone="warning">{sheet.error} این شیت نادیده گرفته می‌شود.</Alert>
                  ) : null}
                  <label>
                    نوع دادهٔ شیت {sheet.name}{' '}
                    <select
                      aria-label={`نوع دادهٔ شیت ${sheet.name}`}
                      disabled={busy || Boolean(sheet.error)}
                      value={selection?.entityClass ?? ''}
                      onChange={(event) => selectSheet(sheet, event.target.value)}
                    >
                      <option value="">نادیده بگیر</option>
                      {allowed.map((item) => (
                        <option key={item.entityClass} value={item.entityClass}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {entity
                    ? entity.writableFields.map((field) => (
                        <label key={field} style={{ display: 'block', marginTop: 8 }}>
                          {datasetFieldLabel(field)}
                          {entity.requiredFields.includes(field) ? ' (الزامی)' : ''}{' '}
                          <select
                            aria-label={`${sheet.name}: ${datasetFieldLabel(field)}`}
                            value={selection?.mapping[field] ?? ''}
                            onChange={(event) => mapField(sheet.index, field, event.target.value)}
                          >
                            <option value="">انتخاب نشده</option>
                            {sheet.headers.map((header) => (
                              <option key={header} value={header}>
                                {header}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))
                    : null}
                </fieldset>
              );
            })}
            {sheets.length ? (
              <p>
                شیت‌های نادیده‌گرفته‌شده:{' '}
                {ignored.map((sheet) => sheet.name).join('، ') || 'هیچ‌کدام'}
              </p>
            ) : null}
            {validation.length ? (
              <Alert tone="warning">
                {validation.map((text, index) => (
                  <div key={index}>{text}</div>
                ))}
              </Alert>
            ) : null}
            <V2Button
              disabled={busy || !file || !selected.length || Boolean(validation.length)}
              onClick={() => void dryRun()}
            >
              پیش‌نمایش مجموعه
            </V2Button>
            {preview && batch ? (
              <>
                <p>
                  خطا: {preview.errorCount.toLocaleString('fa-IR')}؛ هشدار:{' '}
                  {preview.warningCount.toLocaleString('fa-IR')}
                </p>
                {preview.children.map((child) => {
                  const metadata = batch.children.find(
                    (item) => item.importJobId === child.importJobId,
                  );
                  return (
                    <section key={child.importJobId}>
                      <h3>{metadata?.sheetName ?? child.importJobId}</h3>
                      <p>
                        ردیف: {child.dryRun.rowCount}؛ سالم: {child.dryRun.validCount}؛ ردشده:{' '}
                        {child.dryRun.errorCount}؛ هشدار: {child.dryRun.warningCount}؛ تکراری:{' '}
                        {child.dryRun.skippedCount}
                      </p>
                      {child.dryRun.diagnostics.map((diagnostic, index) => (
                        <p key={`d${index}`}>
                          {diagnostic.code}: {diagnostic.message}
                        </p>
                      ))}
                      {(rows[child.importJobId] ?? []).map((row) => (
                        <div key={`${row.sourceRow}:${row.status}`}>
                          ردیف {row.sourceRow}:{' '}
                          {[...row.errors, ...row.warnings]
                            .map((diagnostic) => `${diagnostic.code}: ${diagnostic.message}`)
                            .join('؛ ')}
                        </div>
                      ))}
                      {child.dryRun.errorCount > 100 || child.dryRun.warningCount > 100 ? (
                        <p>نمایش تا ۱۰۰ ردیف از هر نوع خطا و هشدار.</p>
                      ) : null}
                    </section>
                  );
                })}
                {preview.errorCount ? (
                  <Alert tone="danger">
                    خطاهای مجموعه را اصلاح کنید؛ ثبت نهایی تمام شیت‌ها متوقف است.
                  </Alert>
                ) : null}
                {preview.warningCount ? (
                  <label>
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      disabled={busy}
                      onChange={(event) => setAcknowledged(event.target.checked)}
                    />
                    هشدارهای همهٔ شیت‌ها را بررسی و تأیید کردم.
                  </label>
                ) : null}
                <V2Button
                  disabled={
                    busy ||
                    Boolean(preview.errorCount) ||
                    (preview.warningCount > 0 && !acknowledged)
                  }
                  onClick={() => void commit()}
                >
                  ثبت نهایی مجموعه
                </V2Button>
              </>
            ) : null}
          </>
        ) : (
          <Alert tone="success">
            مجموعه ثبت شد. تعداد ثبت‌شده:{' '}
            {result.children
              .reduce((sum, child) => sum + child.committedCount, 0)
              .toLocaleString('fa-IR')}
            ؛ تکراری:{' '}
            {result.children
              .reduce((sum, child) => sum + child.skippedCount, 0)
              .toLocaleString('fa-IR')}
            {result.replayed ? '؛ نتیجهٔ اجرای قبلی' : ''}
          </Alert>
        )}
        <V2Button variant="secondary" disabled={busy} onClick={onBack}>
          بازگشت
        </V2Button>
      </div>
    </V2Card>
  );
}
